import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, TrendingUp, Receipt, Users, CheckCircle, Plus, DollarSign, ShoppingCart, Calendar, Activity as ActivityIcon, Clock, MapPin, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import { designTokens } from '../design/designTokens';
import { batchService, Batch } from '../services/batchService';
import { activityService, Activity } from '../services/activityService';

const Container = styled.div`
  padding: ${designTokens.spacing[6]};
  max-width: 1400px;
  margin: 0 auto;
  font-family: ${designTokens.typography.fontFamily.primary};
  background: ${designTokens.colors.background.secondary};
  color: ${designTokens.colors.text.primary};
  min-height: 100vh;
  
  /* Mobile responsive padding */
  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[4]};
  }
`;

const Header = styled.div`
  margin-bottom: ${designTokens.spacing[8]};
  text-align: center;
  background: ${designTokens.riverPoultry.gradients.primary};
  padding: ${designTokens.spacing[8]} ${designTokens.spacing[6]};
  border-radius: ${designTokens.borderRadius['2xl']};
  box-shadow: ${designTokens.shadows.lg};
  position: relative;
  overflow: hidden;
  border: 1px solid ${designTokens.riverPoultry.brandColors.primaryLight};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.05)"/><circle cx="10" cy="60" r="0.5" fill="rgba(255,255,255,0.05)"/><circle cx="90" cy="40" r="0.5" fill="rgba(255,255,255,0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const Title = styled.h1`
  font-size: ${designTokens.typography.fontSize['4xl']};
  font-weight: ${designTokens.typography.fontWeight.extrabold};
  color: white;
  margin: 0 0 ${designTokens.spacing[3]} 0;
  font-family: ${designTokens.typography.fontFamily.secondary};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
  
  /* Mobile responsive */
  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize['3xl']};
  }
`;

const Subtitle = styled.p`
  font-size: ${designTokens.typography.fontSize.lg};
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: ${designTokens.typography.fontWeight.medium};
  position: relative;
  z-index: 1;
  
  /* Mobile responsive */
  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.base};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${designTokens.spacing[6]};
  margin-bottom: ${designTokens.spacing[8]};

  ${designTokens.mediaQueries.maxLg} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${designTokens.mediaQueries.maxMd} {
    grid-template-columns: 1fr;
    gap: ${designTokens.mobileSpacing[4]};
  }
`;

const StatCard = styled.div`
  background: ${designTokens.colors.surface.primary};
  border-radius: ${designTokens.borderRadius['2xl']};
  padding: ${designTokens.spacing[6]};
  box-shadow: ${designTokens.shadows.md};
  border: 2px solid ${designTokens.colors.border.light};
  transition: ${designTokens.transitions.fast};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${designTokens.riverPoultry.gradients.primary};
  }

  &:hover {
    box-shadow: ${designTokens.shadows.xl};
    transform: translateY(-4px);
    border-color: ${designTokens.riverPoultry.brandColors.green};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[4]};
  margin-bottom: ${designTokens.spacing[5]};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 56px;
  height: 56px;
  border-radius: ${designTokens.borderRadius.xl};
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.color}40;
  box-shadow: 0 4px 12px ${props => props.color}30;
  color: ${props => props.color};
`;

const StatTitle = styled.h3`
  font-size: ${designTokens.typography.fontSize.sm};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  color: ${designTokens.colors.text.secondary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: ${designTokens.typography.letterSpacing.wide};
  font-family: ${designTokens.typography.fontFamily.secondary};
`;

const StatValue = styled.div`
  font-size: ${designTokens.typography.fontSize['4xl']};
  font-weight: ${designTokens.typography.fontWeight.extrabold};
  color: ${designTokens.colors.text.primary};
  margin-bottom: ${designTokens.spacing[1]};
  line-height: ${designTokens.typography.lineHeight.tight};
  font-family: ${designTokens.typography.fontFamily.secondary};
  
  /* Mobile responsive */
  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize['3xl']};
  }
`;

const StatSubtext = styled.div`
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.tertiary};
  font-weight: ${designTokens.typography.fontWeight.medium};
`;

const StatusIndicator = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[2]};
  margin-top: ${designTokens.spacing[2]};
  font-size: ${designTokens.typography.fontSize.sm};
  font-weight: ${designTokens.typography.fontWeight.medium};
  color: ${props => 
    props.status === 'good' ? designTokens.riverPoultry.brandColors.green : 
    props.status === 'warning' ? designTokens.riverPoultry.brandColors.orange : 
    props.status === 'error' ? designTokens.riverPoultry.brandColors.red :
    designTokens.colors.text.secondary
  };
  padding: ${designTokens.spacing[2]} ${designTokens.spacing[3]};
  background: ${props => 
    props.status === 'good' ? `${designTokens.riverPoultry.brandColors.green}10` : 
    props.status === 'warning' ? `${designTokens.riverPoultry.brandColors.orange}10` : 
    props.status === 'error' ? `${designTokens.riverPoultry.brandColors.red}10` :
    `${designTokens.colors.background.secondary}`
  };
  border-radius: ${designTokens.borderRadius.lg};
  border: 1px solid ${props => 
    props.status === 'good' ? `${designTokens.riverPoultry.brandColors.green}30` : 
    props.status === 'warning' ? `${designTokens.riverPoultry.brandColors.orange}30` : 
    props.status === 'error' ? `${designTokens.riverPoultry.brandColors.red}30` :
    designTokens.colors.border.light
  };
`;

const QuickActions = styled.div`
  margin-bottom: ${designTokens.spacing[8]};
`;

const SectionTitle = styled.h2`
  font-size: ${designTokens.typography.fontSize['2xl']};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.colors.text.primary};
  margin: 0 0 ${designTokens.spacing[5]} 0;
  font-family: ${designTokens.typography.fontFamily.secondary};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -${designTokens.spacing[2]};
    left: 0;
    width: 60px;
    height: 3px;
    background: ${designTokens.riverPoultry.gradients.primary};
    border-radius: ${designTokens.borderRadius.full};
  }
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${designTokens.spacing[5]};
  
  ${designTokens.mediaQueries.maxMd} {
    grid-template-columns: 1fr;
    gap: ${designTokens.mobileSpacing[4]};
  }
`;

const ActionCard = styled.div`
  background: ${designTokens.colors.surface.primary};
  border-radius: ${designTokens.borderRadius['2xl']};
  padding: ${designTokens.spacing[6]};
  box-shadow: ${designTokens.shadows.md};
  border: 2px solid ${designTokens.colors.border.light};
  text-align: center;
  cursor: pointer;
  transition: ${designTokens.transitions.fast};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${designTokens.riverPoultry.gradients.secondary};
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${designTokens.shadows.xl};
    border-color: ${designTokens.riverPoultry.brandColors.green};
    
    &::before {
      background: ${designTokens.riverPoultry.gradients.primary};
    }
  }
`;

const ActionIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${designTokens.borderRadius.xl};
  background: ${designTokens.riverPoultry.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0 auto ${designTokens.spacing[5]} auto;
  box-shadow: 0 8px 20px rgba(46, 125, 50, 0.3);
  transition: ${designTokens.transitions.fast};

  ${ActionCard}:hover & {
    transform: scale(1.1);
    box-shadow: 0 12px 24px rgba(46, 125, 50, 0.4);
  }
`;

const ActionTitle = styled.h3`
  font-size: ${designTokens.typography.fontSize.lg};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.colors.text.primary};
  margin: 0 0 ${designTokens.spacing[2]} 0;
  font-family: ${designTokens.typography.fontFamily.secondary};
`;

const ActionDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0;
`;

const RecentBatches = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
`;

const BatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BatchItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #2e7d32;
`;

const BatchInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BatchName = styled.div`
  font-weight: 600;
  color: #333;
`;

const BatchDetails = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const BatchStatus = styled.div<{ status: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => 
    props.status === 'active' ? '#e8f5e8' : 
    props.status === 'completed' ? '#e3f2fd' : '#ffebee'
  };
  color: ${props => 
    props.status === 'active' ? '#2e7d32' : 
    props.status === 'completed' ? '#1976d2' : '#d32f2f'
  };
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const SummaryIcon = styled.div<{ color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${props => props.color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const SummaryTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryItemLabel = styled.span`
  font-size: 0.875rem;
  color: #4a5568;
  font-weight: 500;
`;

const SummaryItemValue = styled.span<{ positive?: boolean; negative?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => 
    props.positive ? '#38a169' : 
    props.negative ? '#e53e3e' : 
    '#1a202c'
  };
`;





const CalendarActivitiesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarWidget = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CalendarTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CalendarNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  color: #666;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const MonthYear = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  min-width: 120px;
  text-align: center;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const DayHeader = styled.div`
  background: #f7fafc;
  padding: 8px 4px;
  text-align: center;
  font-weight: 600;
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DayCell = styled.div<{ hasEvent: boolean; isToday: boolean; isCurrentMonth: boolean; isSelected: boolean }>`
  background: ${props => 
    props.isSelected ? '#e6fffa' :
    props.isToday ? '#fef5e7' : 
    props.isCurrentMonth ? 'white' : '#f7fafc'
  };
  padding: 6px;
  min-height: 36px;
  border: ${props => props.isToday ? '2px solid #38b2ac' : 'none'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.isSelected ? '#e6fffa' : '#f7fafc'};
  }
`;

const DayNumber = styled.div<{ isCurrentMonth: boolean; isToday: boolean }>`
  font-weight: ${props => props.isToday ? '700' : '500'};
  color: ${props => 
    props.isToday ? '#38b2ac' :
    props.isCurrentMonth ? '#1a202c' : '#a0aec0'
  };
  font-size: 0.875rem;
`;

const EventDot = styled.div<{ type: 'chick' | 'vaccination' | 'feeding' | 'cleaning' | 'maintenance' | 'harvest' | 'other' }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${props => {
    switch (props.type) {
      case 'chick': return '#4caf50';
      case 'vaccination': return '#ff9800';
      case 'feeding': return '#9c27b0';
      case 'cleaning': return '#00bcd4';
      case 'maintenance': return '#ff5722';
      case 'harvest': return '#8bc34a';
      default: return '#2196f3';
    }
  }};
  position: absolute;
  bottom: 2px;
`;

const TodaysActivities = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const TodaysHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const TodaysIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #38b2ac15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #38b2ac;
`;

const TodaysTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const TodaysDate = styled.div`
  font-size: 0.875rem;
  color: #718096;
  margin-left: auto;
  font-weight: 500;
`;

const AddActivityButton = styled.button`
  background: #38b2ac;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 8px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    background: #319795;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  color: #718096;
  transition: all 0.2s ease;

  &:hover {
    background: #f7fafc;
    color: #1a202c;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #1a202c;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #38b2ac;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #38b2ac;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #38b2ac;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  ${props => props.variant === 'primary' ? `
    background: #38b2ac;
    color: white;
    &:hover {
      background: #319795;
    }
  ` : `
    background: #f7fafc;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    &:hover {
      background: #edf2f7;
    }
  `}
`;

const TodaysActivityItem = styled.div<{ type: 'chick' | 'vaccination' | 'feeding' | 'cleaning' | 'maintenance' | 'harvest' | 'other' }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${props => {
    switch (props.type) {
      case 'chick': return '#f0fff4';
      case 'vaccination': return '#fffbeb';
      case 'feeding': return '#f3e5f5';
      case 'cleaning': return '#e0f7fa';
      case 'maintenance': return '#fce4ec';
      case 'harvest': return '#f1f8e9';
      default: return '#f7fafc';
    }
  }};
  border-radius: 8px;
  border-left: 3px solid ${props => {
    switch (props.type) {
      case 'chick': return '#38a169';
      case 'vaccination': return '#ed8936';
      case 'feeding': return '#9c27b0';
      case 'cleaning': return '#00bcd4';
      case 'maintenance': return '#ff5722';
      case 'harvest': return '#8bc34a';
      default: return '#4299e1';
    }
  }};
  margin-bottom: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
      switch (props.type) {
        case 'chick': return '#e6fffa';
        case 'vaccination': return '#fef5e7';
        case 'feeding': return '#f3e5f5';
        case 'cleaning': return '#e0f7fa';
        case 'maintenance': return '#fce4ec';
        case 'harvest': return '#f1f8e9';
        default: return '#edf2f7';
      }
    }};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const TodaysActivityIcon = styled.div<{ type: 'chick' | 'vaccination' | 'feeding' | 'cleaning' | 'maintenance' | 'harvest' | 'other' }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${props => {
    switch (props.type) {
      case 'chick': return '#38a16915';
      case 'vaccination': return '#ed893615';
      case 'feeding': return '#9c27b015';
      case 'cleaning': return '#00bcd415';
      case 'maintenance': return '#ff572215';
      case 'harvest': return '#8bc34a15';
      default: return '#4299e115';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => {
    switch (props.type) {
      case 'chick': return '#38a169';
      case 'vaccination': return '#ed8936';
      case 'feeding': return '#9c27b0';
      case 'cleaning': return '#00bcd4';
      case 'maintenance': return '#ff5722';
      case 'harvest': return '#8bc34a';
      default: return '#4299e1';
    }
  }};
`;

const TodaysActivityInfo = styled.div`
  flex: 1;
`;

const TodaysActivityTitle = styled.div`
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 4px;
  font-size: 0.875rem;
`;

const TodaysActivityDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: #718096;
`;

const TodaysActivityTime = styled.div`
  font-size: 0.75rem;
  color: #a0aec0;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NoActivities = styled.div`
  text-align: center;
  padding: 24px 16px;
  color: #666;
`;

const NoActivitiesIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px auto;
  color: #999;
`;

const NoActivitiesText = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 6px;
`;

const NoActivitiesSubtext = styled.div`
  font-size: 0.8rem;
  color: #999;
`;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddActivityModalOpen, setIsAddActivityModalOpen] = useState(false);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [newActivityForm, setNewActivityForm] = useState({
    title: '',
    type: 'other' as 'chick' | 'vaccination' | 'feeding' | 'cleaning' | 'maintenance' | 'harvest' | 'other',
    date: new Date().toISOString().split('T')[0],
    time: '',
    location: '',
    description: '',
    assignedTo: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    estimatedDuration: 1
  });

  // Load batches and activities from the shared services on component mount
  useEffect(() => {
    const loadedBatches = batchService.loadBatches();
    const loadedActivities = activityService.loadActivities();
    setBatches(loadedBatches);
    setActivities(loadedActivities);
  }, []);

  // Handle selected date from URL parameter
  useEffect(() => {
    const selectedDateParam = searchParams.get('selectedDate');
    if (selectedDateParam) {
      const date = new Date(selectedDateParam);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
      }
    }
  }, [searchParams]);

  // Refresh data when component becomes visible (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const loadedBatches = batchService.loadBatches();
        const loadedActivities = activityService.loadActivities();
        setBatches(loadedBatches);
        setActivities(loadedActivities);
      }
    };

    const handleFocus = () => {
      const loadedBatches = batchService.loadBatches();
      const loadedActivities = activityService.loadActivities();
      setBatches(loadedBatches);
      setActivities(loadedActivities);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Calculate dashboard data from actual batch data
  const activeBatches = batches.filter(batch => batch.status === 'active');
  const totalBirds = activeBatches.reduce((sum, batch) => sum + batch.currentBirds, 0);
  const avgWeight = activeBatches.length > 0 ? 
    activeBatches.reduce((sum, batch) => sum + (batch.currentWeight || 0), 0) / activeBatches.length : 0;

  // Calculate dynamic inventory data from feed management
  const calculateInventoryData = () => {
    try {
      const feedData = JSON.parse(localStorage.getItem('feed-management-data') || '{"inventory": []}');
      const inventory = feedData.inventory || [];
      
      const feedTypes = inventory.reduce((acc: any, item: any) => {
        const type = item.feedType.toLowerCase();
        if (type.includes('starter')) {
          acc.starter = (acc.starter || 0) + item.quantity;
        } else if (type.includes('grower')) {
          acc.grower = (acc.grower || 0) + item.quantity;
        } else if (type.includes('finisher')) {
          acc.finisher = (acc.finisher || 0) + item.quantity;
        } else if (type.includes('layer')) {
          acc.layer = (acc.layer || 0) + item.quantity;
        }
        return acc;
      }, {});

      const totalFeed = Object.values(feedTypes).reduce((sum: number, qty: any) => sum + qty, 0);

      return {
    feed: {
          starter: feedTypes.starter || 0,
          grower: feedTypes.grower || 0,
          finisher: feedTypes.finisher || 0,
          layer: feedTypes.layer || 0,
          total: totalFeed
    },
    vaccines: {
          newcastle: 0, // No hardcoded data - start from zero
          gumboro: 0,
          bronchitis: 0,
          total: 0
    },
    equipment: {
          feeders: 0, // No hardcoded data - start from zero
          drinkers: 0,
          heaters: 0,
          total: 0
        }
      };
    } catch (error) {
      console.error('Error calculating inventory data:', error);
      return {
        feed: { starter: 0, grower: 0, finisher: 0, layer: 0, total: 0 },
        vaccines: { newcastle: 0, gumboro: 0, bronchitis: 0, total: 0 },
        equipment: { feeders: 0, drinkers: 0, heaters: 0, total: 0 }
      };
    }
  };

  // Calculate dynamic financial data
  const calculateFinancialData = () => {
    try {
      const farmProfile = JSON.parse(localStorage.getItem('farm-profile-data') || '{"revenueSources": []}');
      const enabledRevenueSources = farmProfile.revenueSources?.filter((source: any) => source.enabled) || [];
      
      // Calculate revenue from batches and activities
      const totalBirds = batches.reduce((sum, batch) => sum + (batch.initialBirds || 0), 0);
      const totalEggs = batches.reduce((sum, batch) => sum + (batch.eggsCollected || 0), 0);
      const activeBatches = batches.filter(batch => batch.status === 'active').length;
      
      let monthlyRevenue = 0;
      enabledRevenueSources.forEach((source: any) => {
        if ('type' in source) {
          switch (source.type) {
            case 'chicken_sales':
              if (source.name.toLowerCase().includes('chicken')) {
                monthlyRevenue += totalBirds * source.estimatedValue * 0.1; // 10% of total value per month
              } else if (source.name.toLowerCase().includes('egg')) {
                monthlyRevenue += totalEggs * source.estimatedValue * 0.1;
              }
              break;
            case 'manure_sales':
              monthlyRevenue += activeBatches * source.estimatedValue * 0.1;
              break;
            case 'training':
              const trainingCount = activities.filter(a => 
                a.title.toLowerCase().includes('training') || 
                a.title.toLowerCase().includes('workshop')
              ).length;
              monthlyRevenue += trainingCount * source.estimatedValue * 0.1;
              break;
            case 'farm_visits':
              const visitCount = activities.filter(a => 
                a.title.toLowerCase().includes('visit') || 
                a.title.toLowerCase().includes('tour')
              ).length;
              monthlyRevenue += visitCount * source.estimatedValue * 0.1;
              break;
            case 'consultancy':
              const consultCount = activities.filter(a => 
                a.title.toLowerCase().includes('consult') || 
                a.title.toLowerCase().includes('advisory')
              ).length;
              monthlyRevenue += consultCount * source.estimatedValue * 0.1;
              break;
          }
        }
      });

      // Calculate expenses based on batches
      const totalFeedConsumption = batches.reduce((sum, batch) => sum + (batch.feedConsumption || 0), 0);
      const feedCost = totalFeedConsumption * 50; // UGX per kg
      const chickCost = totalBirds * 3000 * 0.1; // 10% of total cost per month
      const medicalCost = batches.reduce((sum, batch) => sum + (batch.mortality || 0) * 2000, 0) * 0.1;
      const laborCost = batches.length * 50000 * 0.1; // 10% of total cost per month
      const utilitiesCost = batches.length * 25000 * 0.1;
      const maintenanceCost = batches.length * 15000 * 0.1;
      
      const monthlyExpenses = feedCost + chickCost + medicalCost + laborCost + utilitiesCost + maintenanceCost;
      const monthlyProfit = monthlyRevenue - monthlyExpenses;

      return {
    monthly: {
          income: monthlyRevenue,
          expenses: monthlyExpenses,
          profit: monthlyProfit
    },
    weekly: {
          income: monthlyRevenue / 4,
          expenses: monthlyExpenses / 4,
          profit: monthlyProfit / 4
    },
    daily: {
          income: monthlyRevenue / 30,
          expenses: monthlyExpenses / 30,
          profit: monthlyProfit / 30
        }
      };
    } catch (error) {
      console.error('Error calculating financial data:', error);
      return {
        monthly: { income: 0, expenses: 0, profit: 0 },
        weekly: { income: 0, expenses: 0, profit: 0 },
        daily: { income: 0, expenses: 0, profit: 0 }
      };
    }
  };

  const inventoryData = calculateInventoryData();
  const financialData = calculateFinancialData();

  // Calculate dynamic status indicators
  const calculateHealthyPopulationStatus = () => {
    if (activeBatches.length === 0) return { status: 'neutral', message: 'No active batches' };
    
    // Calculate average mortality rate across active batches
    const totalMortality = activeBatches.reduce((sum, batch) => {
      const mortalityRate = batch.mortalityRate || 0;
      return sum + mortalityRate;
    }, 0);
    
    const avgMortalityRate = totalMortality / activeBatches.length;
    
    // Industry standards: <5% is excellent, 5-8% is good, >8% is concerning
    if (avgMortalityRate < 5) {
      return { status: 'good', message: 'Healthy population' };
    } else if (avgMortalityRate <= 8) {
      return { status: 'warning', message: 'Monitor closely' };
    } else {
      return { status: 'error', message: 'High mortality' };
    }
  };

  const calculateGoodRotationStatus = () => {
    if (activeBatches.length === 0) return { status: 'neutral', message: 'No active batches' };
    
    // Check if batches are properly spaced (good rotation means batches started at different times)
    const startDates = activeBatches.map(batch => new Date(batch.startDate)).sort();
    const timeGaps = [];
    
    for (let i = 1; i < startDates.length; i++) {
      const gap = startDates[i].getTime() - startDates[i-1].getTime();
      timeGaps.push(gap / (1000 * 60 * 60 * 24)); // Convert to days
    }
    
    if (timeGaps.length === 0) {
      return { status: 'good', message: 'Good rotation' };
    }
    
    const avgGap = timeGaps.reduce((sum, gap) => sum + gap, 0) / timeGaps.length;
    
    // Good rotation: 7-14 days between batches
    if (avgGap >= 7 && avgGap <= 14) {
      return { status: 'good', message: 'Good rotation' };
    } else if (avgGap < 7) {
      return { status: 'warning', message: 'Too frequent' };
    } else {
      return { status: 'warning', message: 'Long gaps' };
    }
  };

  const calculateOnTargetWeightStatus = () => {
    if (activeBatches.length === 0) return { status: 'neutral', message: 'No active batches' };
    
    // Calculate average weight performance vs standards
    const weightPerformances = activeBatches.map(batch => {
      const currentWeight = batch.currentWeight || batch.weight || 0;
      const targetWeight = batch.targetWeight || 0;
      
      if (targetWeight === 0) return 0;
      
      // Calculate percentage of target achieved
      return (currentWeight / targetWeight) * 100;
    }).filter(perf => perf > 0);
    
    if (weightPerformances.length === 0) return { status: 'neutral', message: 'No weight data' };
    
    const avgPerformance = weightPerformances.reduce((sum, perf) => sum + perf, 0) / weightPerformances.length;
    
    // On target: 90-110% of target weight
    if (avgPerformance >= 90 && avgPerformance <= 110) {
      return { status: 'good', message: 'On target' };
    } else if (avgPerformance < 90) {
      return { status: 'warning', message: 'Below target' };
    } else {
      return { status: 'warning', message: 'Above target' };
    }
  };

  const healthyPopulationStatus = calculateHealthyPopulationStatus();
  const goodRotationStatus = calculateGoodRotationStatus();
  const onTargetWeightStatus = calculateOnTargetWeightStatus();

  const dashboardData = {
    totalBirds,
    activeBatches: activeBatches.length,
    avgWeight: avgWeight,
    feedInventory: inventoryData.feed.total,
    monthlyExpenses: financialData.monthly.expenses,
    productionRate: batches.length > 0 ? (activeBatches.length / batches.length) * 100 : 0,
  };

  // Get activities for the selected date from state
  const selectedDateActivities = activities.filter(activity => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    return (activity.startDate || activity.scheduledDate) <= selectedDateStr && (activity.endDate || activity.scheduledDate) >= selectedDateStr;
  });


  // Calendar utility functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return activities.filter(activity => 
      (activity.startDate || activity.scheduledDate) <= dateStr && (activity.endDate || activity.scheduledDate) >= dateStr
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleAddActivity = () => {
    setIsAddActivityModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddActivityModalOpen(false);
    setNewActivityForm({
      title: '',
      type: 'other',
      date: new Date().toISOString().split('T')[0],
      time: '',
      location: '',
      description: '',
      assignedTo: '',
      priority: 'medium',
      estimatedDuration: 1
    });
  };

  const handleSaveActivity = () => {
    if (!newActivityForm.title || !newActivityForm.assignedTo || !newActivityForm.location) {
      // Validation error - form will show inline errors
      return;
    }

    activityService.addActivity({
      title: newActivityForm.title,
      description: newActivityForm.description,
      type: newActivityForm.type,
      status: 'pending',
      priority: newActivityForm.priority,
      scheduledDate: newActivityForm.date,
      startDate: newActivityForm.date,
      endDate: newActivityForm.date,
      startTime: newActivityForm.time,
      endTime: newActivityForm.time,
      location: newActivityForm.location,
      assignedTo: newActivityForm.assignedTo,
      estimatedDuration: newActivityForm.estimatedDuration
    });

    // Refresh activities state
    const loadedActivities = activityService.loadActivities();
    setActivities(loadedActivities);
    
    // Activity added successfully
    handleCloseModal();
  };

  const handleClearActivities = () => {
    if (window.confirm('Are you sure you want to clear all activities? This action cannot be undone.')) {
      activityService.forceReset();
      setActivities([]);
      // Force refresh the page to ensure clean state
      window.location.reload();
    }
  };

  // Use actual batch data for recent batches
  const recentBatches = activeBatches.slice(0, 5).map(batch => ({
    id: batch.id,
    name: batch.name,
    age: batch.age,
    birds: batch.currentBirds,
    weight: batch.currentWeight,
    status: batch.status
  }));

  const quickActions = [
    {
      title: 'Add New Batch',
      description: 'Start tracking a new broiler batch',
      icon: <Plus size={24} />,
      action: () => navigate('/batch-management?openForm=true')
    },
    {
      title: 'Record Weight',
      description: 'Log daily weight measurements',
      icon: <TrendingUp size={24} />,
      action: () => navigate('/growth-tracking?openForm=true')
    },
    {
      title: 'Feed Consumption',
      description: 'Track daily feed usage',
      icon: <Package size={24} />,
      action: () => navigate('/feed-management?openForm=true')
    },
    {
      title: 'Add Expense',
      description: 'Record farm expenses',
      icon: <Receipt size={24} />,
      action: () => navigate('/expense-tracking?openForm=true')
    },
    {
      title: 'Clear Activities',
      description: 'Start fresh with no activities',
      icon: <XCircle size={24} />,
      action: handleClearActivities
    }
  ];

  return (
    <Container>
        <Header>
          <Title>Farm Dashboard</Title>
          <Subtitle>Overview of your poultry farm operations</Subtitle>
        </Header>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="#38a169">
              <Users size={24} />
            </StatIcon>
            <StatTitle>Total Birds</StatTitle>
          </StatHeader>
          <StatValue>{dashboardData.totalBirds.toLocaleString()}</StatValue>
          <StatSubtext>Across all batches</StatSubtext>
          <StatusIndicator status={healthyPopulationStatus.status}>
            {healthyPopulationStatus.status === 'good' ? <CheckCircle size={16} /> : 
             healthyPopulationStatus.status === 'warning' ? <Clock size={16} /> : 
             healthyPopulationStatus.status === 'error' ? <XCircle size={16} /> :
             <ActivityIcon size={16} />}
            {healthyPopulationStatus.message}
          </StatusIndicator>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#4299e1">
              <Package size={24} />
            </StatIcon>
            <StatTitle>Active Batches</StatTitle>
          </StatHeader>
          <StatValue>{dashboardData.activeBatches}</StatValue>
          <StatSubtext>Currently running</StatSubtext>
          <StatusIndicator status={goodRotationStatus.status}>
            {goodRotationStatus.status === 'good' ? <CheckCircle size={16} /> : 
             goodRotationStatus.status === 'warning' ? <Clock size={16} /> : 
             goodRotationStatus.status === 'error' ? <XCircle size={16} /> :
             <ActivityIcon size={16} />}
            {goodRotationStatus.message}
          </StatusIndicator>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#ed8936">
              <TrendingUp size={24} />
            </StatIcon>
            <StatTitle>Average Weight</StatTitle>
          </StatHeader>
          <StatValue>{dashboardData.avgWeight} kg</StatValue>
          <StatSubtext>Current average</StatSubtext>
          <StatusIndicator status={onTargetWeightStatus.status}>
            {onTargetWeightStatus.status === 'good' ? <CheckCircle size={16} /> : 
             onTargetWeightStatus.status === 'warning' ? <Clock size={16} /> : 
             onTargetWeightStatus.status === 'error' ? <XCircle size={16} /> :
             <ActivityIcon size={16} />}
            {onTargetWeightStatus.message}
          </StatusIndicator>
        </StatCard>
      </StatsGrid>

      <CalendarActivitiesGrid>
        <CalendarWidget>
          <CalendarHeader>
            <CalendarTitle>
              <Calendar size={24} />
              Calendar
            </CalendarTitle>
            <CalendarNav>
              <NavButton onClick={() => navigateMonth('prev')}>
                <ChevronLeft size={20} />
              </NavButton>
              <MonthYear>{formatMonthYear(currentDate)}</MonthYear>
              <NavButton onClick={() => navigateMonth('next')}>
                <ChevronRight size={20} />
              </NavButton>
            </CalendarNav>
          </CalendarHeader>
          
          <CalendarGrid>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}
            {getDaysInMonth(currentDate).map((day, index) => {
              if (!day) {
                return <DayCell key={index} hasEvent={false} isToday={false} isCurrentMonth={false} isSelected={false} />;
              }
              
              const events = getEventsForDate(day);
              const isToday = day.toDateString() === new Date().toDateString();
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isSelected = day.toDateString() === selectedDate.toDateString();
              
              return (
                <DayCell 
                  key={index} 
                  hasEvent={events.length > 0} 
                  isToday={isToday}
                  isCurrentMonth={isCurrentMonth}
                  isSelected={isSelected}
                  onClick={() => setSelectedDate(day)}
                >
                  <DayNumber isCurrentMonth={isCurrentMonth} isToday={isToday}>
                    {day.getDate()}
                  </DayNumber>
                  {events.map((event, eventIndex) => (
                    <EventDot key={eventIndex} type={event.type} />
                  ))}
                </DayCell>
              );
            })}
          </CalendarGrid>
        </CalendarWidget>

        <TodaysActivities>
          <TodaysHeader>
            <TodaysIcon>
              <Clock size={24} />
            </TodaysIcon>
            <TodaysTitle>
              {selectedDate.toDateString() === new Date().toDateString() 
                ? "Today's Activities" 
                : "Selected Date Activities"}
            </TodaysTitle>
            <TodaysDate>{selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</TodaysDate>
            <AddActivityButton onClick={handleAddActivity}>
              <Plus size={14} />
              Add Activity
            </AddActivityButton>
          </TodaysHeader>
          
          {selectedDateActivities.length > 0 ? (
            selectedDateActivities.map(activity => (
              <TodaysActivityItem key={activity.id} type={activity.type}>
                <TodaysActivityIcon type={activity.type}>
                  {activity.type === 'chick' ? <Users size={18} /> :
                   activity.type === 'vaccination' ? <ActivityIcon size={18} /> :
                   <Calendar size={18} />}
                </TodaysActivityIcon>
                <TodaysActivityInfo>
                  <TodaysActivityTitle>{activity.title}</TodaysActivityTitle>
                  <TodaysActivityDetails>
                    <span><MapPin size={14} /> {activity.location}</span>
                  </TodaysActivityDetails>
                </TodaysActivityInfo>
                <TodaysActivityTime>
                  <Clock size={14} />
                  {activity.startTime}
                </TodaysActivityTime>
              </TodaysActivityItem>
            ))
          ) : (
            <NoActivities>
              <NoActivitiesIcon>
                <Calendar size={24} />
              </NoActivitiesIcon>
              <NoActivitiesText>
                {selectedDate.toDateString() === new Date().toDateString() 
                  ? "No activities scheduled for today" 
                  : "No activities scheduled for this date"}
              </NoActivitiesText>
              <NoActivitiesSubtext>
                {selectedDate.toDateString() === new Date().toDateString() 
                  ? "Enjoy your free day!" 
                  : "Click on a date in the calendar to view activities"}
              </NoActivitiesSubtext>
            </NoActivities>
          )}
        </TodaysActivities>
      </CalendarActivitiesGrid>

      <SummaryGrid>
        <SummaryCard>
          <SummaryHeader>
            <SummaryIcon color="#4caf50">
              <ShoppingCart size={24} />
            </SummaryIcon>
            <SummaryTitle>Inventory Summary</SummaryTitle>
          </SummaryHeader>
          
          <SummaryItem>
            <SummaryItemLabel>Feed (kg)</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.feed.total.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Starter Feed</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.feed.starter} kg</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Grower Feed</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.feed.grower} kg</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Finisher Feed</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.feed.finisher} kg</SummaryItemValue>
          </SummaryItem>
          
          <SummaryItem>
            <SummaryItemLabel>Vaccines (doses)</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.vaccines.total}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Newcastle</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.vaccines.newcastle}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Gumboro</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.vaccines.gumboro}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Bronchitis</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.vaccines.bronchitis}</SummaryItemValue>
          </SummaryItem>
          
          <SummaryItem>
            <SummaryItemLabel>Equipment (units)</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.equipment.total}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Feeders</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.equipment.feeders}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Drinkers</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.equipment.drinkers}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>• Heaters</SummaryItemLabel>
            <SummaryItemValue>{inventoryData.equipment.heaters}</SummaryItemValue>
          </SummaryItem>
        </SummaryCard>

        <SummaryCard>
          <SummaryHeader>
            <SummaryIcon color="#2196f3">
              <DollarSign size={24} />
            </SummaryIcon>
            <SummaryTitle>Financial Summary</SummaryTitle>
          </SummaryHeader>
          
          <SummaryItem>
            <SummaryItemLabel>Monthly Income</SummaryItemLabel>
            <SummaryItemValue positive>UGX {financialData.monthly.income.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>Monthly Expenses</SummaryItemLabel>
            <SummaryItemValue negative>UGX {financialData.monthly.expenses.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>Monthly Profit</SummaryItemLabel>
            <SummaryItemValue positive>UGX {financialData.monthly.profit.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          
          <SummaryItem>
            <SummaryItemLabel>Weekly Income</SummaryItemLabel>
            <SummaryItemValue positive>UGX {financialData.weekly.income.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>Weekly Expenses</SummaryItemLabel>
            <SummaryItemValue negative>UGX {financialData.weekly.expenses.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>Weekly Profit</SummaryItemLabel>
            <SummaryItemValue positive>UGX {financialData.weekly.profit.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          
          <SummaryItem>
            <SummaryItemLabel>Daily Income</SummaryItemLabel>
            <SummaryItemValue positive>UGX {financialData.daily.income.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>Daily Expenses</SummaryItemLabel>
            <SummaryItemValue negative>UGX {financialData.daily.expenses.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryItemLabel>Daily Profit</SummaryItemLabel>
            <SummaryItemValue positive>UGX {financialData.daily.profit.toLocaleString()}</SummaryItemValue>
          </SummaryItem>
        </SummaryCard>
      </SummaryGrid>


      <QuickActions>
        <SectionTitle>Quick Actions</SectionTitle>
        <ActionsGrid>
          {quickActions.map((action, index) => (
            <ActionCard key={index} onClick={action.action}>
              <ActionIcon>
                {action.icon}
              </ActionIcon>
              <ActionTitle>{action.title}</ActionTitle>
              <ActionDescription>{action.description}</ActionDescription>
            </ActionCard>
          ))}
        </ActionsGrid>
      </QuickActions>

      <RecentBatches>
        <SectionTitle>Recent Batches</SectionTitle>
        <BatchList>
          {recentBatches.map(batch => (
            <BatchItem key={batch.id}>
              <BatchInfo>
                <BatchName>{batch.name}</BatchName>
                <BatchDetails>
                  {batch.age} days • {batch.birds} birds • {batch.weight} kg avg
                </BatchDetails>
              </BatchInfo>
              <BatchStatus status={batch.status}>{batch.status}</BatchStatus>
            </BatchItem>
          ))}
        </BatchList>
      </RecentBatches>

      <Modal isOpen={isAddActivityModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Add New Activity</ModalTitle>
            <CloseButton onClick={handleCloseModal}>
              ✕
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>Activity Title *</Label>
            <Input
              type="text"
              value={newActivityForm.title}
              onChange={(e) => setNewActivityForm({ ...newActivityForm, title: e.target.value })}
              placeholder="e.g., Chick Arrival - Batch C"
            />
          </FormGroup>

          <FormGroup>
            <Label>Activity Type</Label>
            <Select
              value={newActivityForm.type}
              onChange={(e) => setNewActivityForm({ ...newActivityForm, type: e.target.value as 'chick' | 'vaccination' | 'other' })}
            >
              <option value="chick">Chick Arrival</option>
              <option value="vaccination">Vaccination</option>
              <option value="other">Other Activity</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Date</Label>
            <Input
              type="date"
              value={newActivityForm.date}
              onChange={(e) => setNewActivityForm({ ...newActivityForm, date: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Time *</Label>
            <Input
              type="time"
              value={newActivityForm.time}
              onChange={(e) => setNewActivityForm({ ...newActivityForm, time: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Location *</Label>
            <Input
              type="text"
              value={newActivityForm.location}
              onChange={(e) => setNewActivityForm({ ...newActivityForm, location: e.target.value })}
              placeholder="e.g., Hatchery Building"
            />
          </FormGroup>

          <FormGroup>
            <Label>Assigned To *</Label>
            <Input
              type="text"
              value={newActivityForm.assignedTo}
              onChange={(e) => setNewActivityForm({ ...newActivityForm, assignedTo: e.target.value })}
              placeholder="e.g., Farm Manager, John Doe"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <TextArea
              value={newActivityForm.description}
              onChange={(e) => setNewActivityForm({ ...newActivityForm, description: e.target.value })}
              placeholder="Additional details about the activity..."
            />
          </FormGroup>

          <ButtonGroup>
            <Button onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveActivity}>
              Add Activity
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default Dashboard;